import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Bell,
  BookOpen,
  ChevronRight,
  Clock,
  Clock3,
  FileText,
  Fingerprint,
  Layers,
  LogIn,
  Monitor,
  PhoneCall,
  RefreshCw,
  Smartphone,
  Sparkles,
  Sun,
  UserCheck,
  Users,
  Wallet,
} from "lucide-react";
import { hasPermission } from "../../utils/permissionUtils";
import { getMyDevices, getMyLoginLogs } from "../../api/auth";
import { getCrmDashboard } from "../../api/crm/dashboard";
import { getHrmDashboard } from "../../api/hrm/dashboard";
import { getElearningDashboard } from "../../api/elearning/dashboard";
import { getFinanceDashboard } from "../../api/finance/dashboard";
import { getAttendanceStatus } from "../../api/hrm/employees/attendanceLog";
import { getUnreadCount } from "../../api/system/notifications";
import { startMobileHomeTour } from "./mobileHomeTour";
import {
  MobilePageShell,
  MobileSectionHeader,
  MobileStatCard,
  getTone,
} from "./MobileNavPrimitives";

function displayName(userName) {
  if (!userName) return "Người dùng";
  const parts = userName.trim().split(/\s+/);
  if (parts.length < 2) return userName;
  return `${parts[parts.length - 1]} ${parts[0]}`;
}

function fmtHours(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}h`;
}

function fmtDays(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}n`;
}

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 1 })}%`;
}

function formatCurrencyCompact(value) {
  const amount = Number(value || 0);
  const abs = Math.abs(amount);
  if (abs >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toLocaleString("vi-VN", {
      maximumFractionDigits: 1,
    })} tỷ`;
  }
  if (abs >= 1_000_000) {
    return `${(amount / 1_000_000).toLocaleString("vi-VN", {
      maximumFractionDigits: 1,
    })} triệu`;
  }
  return `${amount.toLocaleString("vi-VN")} ₫`;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: vi });
  } catch {
    return "—";
  }
}

function shortUserAgent(ua) {
  if (!ua) return "—";
  const s = String(ua);
  if (s.length <= 64) return s;
  return `${s.slice(0, 61)}…`;
}

function deviceClassLabel(deviceClass) {
  const cls = String(deviceClass || "").toLowerCase();
  if (cls === "desktop") return "Máy tính";
  if (cls === "mobile") return "Điện thoại";
  return "Thiết bị";
}

function loginStatusMeta(status) {
  const s = String(status || "").toLowerCase();
  if (s === "success") {
    return {
      text: "Thành công",
      cls: "text-emerald-800 bg-emerald-100 border border-emerald-200",
    };
  }
  if (s === "failed") {
    return {
      text: "Thất bại",
      cls: "text-rose-800 bg-rose-100 border border-rose-200",
    };
  }
  return {
    text: status || "—",
    cls: "text-gray-700 bg-gray-100 border border-gray-200",
  };
}

function JournalSection({ title, tone, children }) {
  return (
    <section>
      <MobileSectionHeader title={title} tone={tone} />
      <div className="mt-3">{children}</div>
    </section>
  );
}

function JournalList({ items, emptyText, renderItem }) {
  if (!items.length) {
    return (
      <p className="rounded-2xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-[12px] font-medium text-gray-500">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm divide-y divide-gray-100">
      {items.map(renderItem)}
    </div>
  );
}

function PersonalAccountPanel({ loading, devices, loginLogs, showNoDashNote }) {
  return (
    <div data-tour="m-home-personal" className="space-y-6">
      <div>
        <MobileSectionHeader title="Tài khoản của tôi" tone="rose" />
        <p className="mb-1 -mt-1 pl-3.5 text-[11px] leading-relaxed text-gray-600">
          {showNoDashNote
            ? "Bạn chưa có quyền xem Dashboard module. Dưới đây là thiết bị và nhật ký đăng nhập gần đây của tài khoản."
            : "Thiết bị và nhật ký đăng nhập gần đây của tài khoản."}
        </p>
      </div>

      <JournalSection title="Thiết bị sử dụng" tone="sky">
        {loading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <div
                key={i}
                className="h-[72px] animate-pulse rounded-2xl bg-sky-100"
              />
            ))}
          </div>
        ) : (
          <JournalList
            items={devices}
            emptyText="Chưa ghi nhận thiết bị đăng nhập."
            renderItem={(device, index) => {
              const DeviceIcon =
                device.device_class === "mobile" ? Smartphone : Monitor;
              return (
                <div
                  key={device.id || `device-${index}`}
                  className="flex items-start gap-3 px-4 py-3.5"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-600 text-white shadow-sm shadow-sky-200">
                    <DeviceIcon size={17} strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-gray-900">
                      {deviceClassLabel(device.device_class)}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[11px] text-gray-600">
                      {shortUserAgent(device.user_agent)}
                    </p>
                    <p className="mt-1 text-[10px] font-medium text-gray-500">
                      Lần cuối: {formatDateTime(device.last_seen_at)}
                    </p>
                  </div>
                </div>
              );
            }}
          />
        )}
      </JournalSection>

      <JournalSection title="Log đăng nhập" tone="violet">
        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-[68px] animate-pulse rounded-2xl bg-violet-100"
              />
            ))}
          </div>
        ) : (
          <JournalList
            items={loginLogs}
            emptyText="Chưa có nhật ký đăng nhập."
            renderItem={(log) => {
              const status = loginStatusMeta(log.status);
              return (
                <div
                  key={log.id}
                  className="flex items-start gap-3 px-4 py-3.5"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm shadow-violet-200">
                    <LogIn size={17} strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[13px] font-bold text-gray-900">
                        {formatDateTime(log.login_time)}
                      </p>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${status.cls}`}
                      >
                        {status.text}
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11px] font-medium text-gray-600">
                      IP: {log.ip_address || "—"}
                    </p>
                    <p className="mt-0.5 line-clamp-2 text-[10px] text-gray-500">
                      {shortUserAgent(log.user_agent)}
                    </p>
                  </div>
                </div>
              );
            }}
          />
        )}
      </JournalSection>
    </div>
  );
}

function ModuleReportSection({
  title,
  subtitle,
  tone,
  hubPath,
  loading,
  cards,
  dataTour,
  onOpenHub,
}) {
  const t = getTone(tone);

  return (
    <section
      data-tour={dataTour}
      className="rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <span className={`h-5 w-1.5 shrink-0 rounded-full ${t.accent}`} />
            <h3 className="text-[15px] font-bold text-gray-900">{title}</h3>
          </div>
          {subtitle ? (
            <p className="mt-0.5 pl-4 text-[11px] font-medium text-gray-600">
              {subtitle}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onOpenHub}
          className={`shrink-0 rounded-full border px-3 py-1.5 text-[11px] font-bold active:brightness-95 ${t.banner || t.chip} ${t.text}`}
        >
          Chi tiết
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-[104px] animate-pulse rounded-2xl ${t.softBg}`}
            />
          ))}
        </div>
      ) : cards.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {cards.map((card) => (
            <MobileStatCard
              key={card.key}
              label={card.label}
              value={card.value}
              hint={card.hint}
              icon={card.icon}
              tone={card.tone}
              onClick={card.onClick}
            />
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={onOpenHub}
          className={`flex w-full items-center justify-between rounded-2xl border border-dashed px-4 py-6 text-left ${t.banner || t.chip}`}
        >
          <p className="text-[12px] font-medium text-gray-600">
            Chưa có chỉ số — mở hub {title}
          </p>
          <ChevronRight size={16} className="text-gray-400" />
        </button>
      )}
    </section>
  );
}

export default function HomeScreen() {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const canCrmDash = hasPermission("crm.dashboard", "R");
  const canHrmDash = hasPermission("hrm.dashboard", "R");
  const canElearnDash = hasPermission("elearning.dashboard", "R");
  const canFinanceDash = hasPermission("finance.dashboard", "R");
  const canAttendance = hasPermission("hrm.attendance", "R");
  const canNotify = hasPermission("system.notifications", "R");

  const [crmStats, setCrmStats] = useState(null);
  const [hrmStats, setHrmStats] = useState(null);
  const [elearnStats, setElearnStats] = useState(null);
  const [financeStats, setFinanceStats] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [unread, setUnread] = useState(null);
  const [myDevices, setMyDevices] = useState([]);
  const [myLoginLogs, setMyLoginLogs] = useState([]);
  const [personalLoading, setPersonalLoading] = useState(false);

  const handleQuickTour = useCallback(() => {
    window.setTimeout(() => startMobileHomeTour(), 80);
  }, []);

  const hasAnyModuleDash =
    canCrmDash || canHrmDash || canElearnDash || canFinanceDash;

  const loadPersonalActivity = useCallback(async () => {
    setPersonalLoading(true);
    try {
      const [devicesData, logsData] = await Promise.all([
        getMyDevices().catch(() => ({ devices: [] })),
        getMyLoginLogs({ page: 1, limit: 8 }).catch(() => ({ items: [] })),
      ]);
      setMyDevices(Array.isArray(devicesData?.devices) ? devicesData.devices : []);
      setMyLoginLogs(Array.isArray(logsData?.items) ? logsData.items : []);
    } finally {
      setPersonalLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      let nextCrm = null;
      let nextHrm = null;
      let nextElearn = null;
      let nextFinance = null;
      let nextAttendance = null;
      let nextUnread = null;
      const jobs = [];
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;

      if (canCrmDash) {
        jobs.push(
          getCrmDashboard({ year, month, branch_id: "all" })
            .then((data) => {
              nextCrm = data?.stats || null;
            })
            .catch(() => {})
        );
      }

      if (canHrmDash) {
        jobs.push(
          getHrmDashboard({ year, month, branch_id: "all" })
            .then((data) => {
              nextHrm = data?.stats || null;
            })
            .catch(() => {})
        );
      }

      if (canElearnDash) {
        jobs.push(
          getElearningDashboard({ year, branch_id: "all" })
            .then((data) => {
              nextElearn = data?.stats || null;
            })
            .catch(() => {})
        );
      }

      if (canFinanceDash) {
        jobs.push(
          getFinanceDashboard({ year, month, branch_id: "all" })
            .then((data) => {
              nextFinance = data?.stats || null;
            })
            .catch(() => {})
        );
      }

      if (canAttendance) {
        jobs.push(
          getAttendanceStatus()
            .then((data) => {
              nextAttendance = data;
            })
            .catch(() => {})
        );
      }

      if (canNotify) {
        jobs.push(
          getUnreadCount()
            .then((data) => {
              nextUnread = Number(data?.count ?? 0);
            })
            .catch(() => {})
        );
      }

      jobs.push(loadPersonalActivity());

      await Promise.all(jobs);
      setCrmStats(nextCrm);
      setHrmStats(nextHrm);
      setElearnStats(nextElearn);
      setFinanceStats(nextFinance);
      setAttendance(nextAttendance);
      setUnread(nextUnread);
      setLoading(false);
      setRefreshing(false);
    },
    [
      canCrmDash,
      canHrmDash,
      canElearnDash,
      canFinanceDash,
      canAttendance,
      canNotify,
      loadPersonalActivity,
    ]
  );

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUserName(parsed?.name || parsed?.email || "Người dùng");
      }
    } catch {
      setUserName("Người dùng");
    }
    loadDashboard(false);
  }, [loadDashboard]);

  const crmCards = useMemo(() => {
    if (!canCrmDash || !crmStats) return [];
    const s = crmStats;
    const cards = [
      {
        key: "customers",
        label: "Khách hàng",
        value: String(s.customers?.total ?? 0),
        hint:
          s.customers?.newThisMonth != null
            ? `+${s.customers.newThisMonth} mới tháng này`
            : "Tổng hồ sơ",
        icon: Users,
        tone: "sky",
        onClick: () => navigate("/crm/customers"),
      },
      {
        key: "followups",
        label: "Follow-up",
        value: String(s.contacts?.upcomingFollowups ?? 0),
        hint: "Lịch chăm sóc sắp tới",
        icon: PhoneCall,
        tone: "amber",
        onClick: () => navigate("/m/crm/followups"),
      },
      {
        key: "contracts",
        label: "HĐ đang chạy",
        value: String(s.contracts?.active ?? 0),
        hint:
          s.contracts?.expiringSoon != null
            ? `${s.contracts.expiringSoon} sắp hết hạn`
            : "Đang thực hiện",
        icon: FileText,
        tone: "emerald",
        onClick: () => navigate("/crm/contracts"),
      },
      {
        key: "contacts",
        label: "Liên hệ tháng",
        value: String(s.contacts?.thisMonth ?? 0),
        hint: "Hoạt động CRM",
        icon: PhoneCall,
        tone: "violet",
        onClick: () => navigate("/m/crm"),
      },
    ];
    return cards;
  }, [canCrmDash, crmStats, navigate]);

  const hrmCards = useMemo(() => {
    if (!canHrmDash || !hrmStats) return [];
    const s = hrmStats;
    return [
      {
        key: "attendance",
        label: "Chấm công tháng",
        value: fmtHours(s.attendanceHoursMonth),
        hint:
          s.attendancePeopleMonth != null
            ? `${s.attendancePeopleMonth} người`
            : "Giờ công",
        icon: Clock,
        tone: "emerald",
        onClick: () => navigate("/hrm/hrm_attendance"),
      },
      {
        key: "overtime",
        label: "Tăng ca tháng",
        value: fmtHours(s.overtimeHoursMonth),
        hint:
          s.overtimePeopleMonth != null
            ? `${s.overtimePeopleMonth} người`
            : "Giờ OT",
        icon: Clock,
        tone: "amber",
        onClick: () => navigate("/hrm/overtime"),
      },
      {
        key: "leave",
        label: "Nghỉ & công tác",
        value: fmtDays(s.leaveTripDaysMonth),
        hint:
          s.leaveTripPeopleMonth != null
            ? `${s.leaveTripPeopleMonth} người`
            : "Ngày trong tháng",
        icon: Sun,
        tone: "rose",
        onClick: () => navigate("/hrm/on_leave"),
      },
      {
        key: "employees",
        label: "Nhân viên",
        value: String(s.employees?.active ?? s.employees?.total ?? "—"),
        hint:
          s.employees?.total != null
            ? `${s.employees.total} tổng`
            : "Đang hoạt động",
        icon: Users,
        tone: "violet",
        onClick: () => navigate("/hrm/employees"),
      },
    ];
  }, [canHrmDash, hrmStats, navigate]);

  const elearnCards = useMemo(() => {
    if (!canElearnDash || !elearnStats) return [];
    const s = elearnStats;
    return [
      {
        key: "students",
        label: "Học viên",
        value: String(s.students_active ?? "—"),
        hint: "Đang theo lớp",
        icon: Users,
        tone: "sky",
        onClick: () => navigate("/elearning/students"),
      },
      {
        key: "classes",
        label: "Lớp học",
        value: String(s.classes_active ?? "—"),
        hint: "Trong phạm vi",
        icon: Layers,
        tone: "emerald",
        onClick: () => navigate("/elearning/classes"),
      },
      {
        key: "courses",
        label: "Khóa học",
        value: String(s.courses_open ?? "—"),
        hint: "Có lớp mở",
        icon: BookOpen,
        tone: "indigo",
        onClick: () => navigate("/elearning/courses"),
      },
      {
        key: "attendance",
        label: "Điểm danh",
        value: fmtPct(s.attendance_rate_pct),
        hint: "Tỉ lệ trong phạm vi",
        icon: UserCheck,
        tone: "teal",
        onClick: () => navigate("/elearning/edu_attendance"),
      },
    ];
  }, [canElearnDash, elearnStats, navigate]);

  const financeCards = useMemo(() => {
    if (!canFinanceDash || !financeStats) return [];
    const s = financeStats;
    return [
      {
        key: "waiting",
        label: "Chờ duyệt",
        value: String(s.approvals?.waitingCount ?? 0),
        hint: `${s.approvals?.waitingPayments ?? 0} TT · ${
          s.approvals?.waitingAdvances ?? 0
        } TU`,
        icon: Clock3,
        tone: "amber",
        onClick: () => navigate("/finance/payment_requests"),
      },
      {
        key: "receipts",
        label: "Thực thu tháng",
        value: formatCurrencyCompact(s.receipts?.monthAmount),
        hint:
          s.receipts?.monthCount != null
            ? `${s.receipts.monthCount} phiếu`
            : "Thu trong tháng",
        icon: ArrowUpCircle,
        tone: "emerald",
        onClick: () => navigate("/finance/income_expenditure"),
      },
      {
        key: "outflow",
        label: "Chi đã duyệt",
        value: formatCurrencyCompact(s.outflow?.monthAmount),
        hint: "Thanh toán + tạm ứng",
        icon: ArrowDownCircle,
        tone: "rose",
        onClick: () => navigate("/finance/payment_requests"),
      },
      {
        key: "advances",
        label: "Tạm ứng hoàn tất",
        value: String(s.advances?.completedMonthCount ?? 0),
        hint: "Trong tháng",
        icon: Wallet,
        tone: "teal",
        onClick: () => navigate("/finance/advance"),
      },
    ];
  }, [canFinanceDash, financeStats, navigate]);

  const attendanceHint = attendance
    ? attendance.can_checkin
      ? attendance.message || "Có thể chấm công"
      : attendance.message || "Đã chấm công / ngoài khung giờ"
    : null;

  const dateLabel = format(new Date(), "EEEE, dd/MM/yyyy", { locale: vi });

  return (
    <MobilePageShell>
      <div className="relative overflow-hidden border-b border-rose-200 bg-gradient-to-br from-rose-50 via-white to-white px-5 pb-5 pt-6">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-rose-200/40 blur-2xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -left-6 bottom-0 h-24 w-24 rounded-full bg-sky-200/30 blur-xl"
        />
        <div className="relative flex items-start justify-between gap-3">
          <div data-tour="m-home-greeting" className="min-w-0 flex-1">
            <div className="mb-1.5 inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-100 px-2.5 py-0.5">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-600" />
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-rose-800">
                Tổng quan
              </p>
            </div>
            <h2 className="text-lg font-bold leading-snug text-gray-900">
              Xin chào,{" "}
              <span className="text-rose-700">{displayName(userName)}</span>
            </h2>
            <p className="mt-1 text-[12px] font-medium capitalize text-gray-600">
              {dateLabel}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => loadDashboard(true)}
              disabled={refreshing || loading}
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 shadow-sm active:bg-gray-50 disabled:opacity-50"
              aria-label="Làm mới"
            >
              <RefreshCw
                size={16}
                className={refreshing ? "animate-spin" : undefined}
              />
            </button>
            <button
              type="button"
              onClick={handleQuickTour}
              title="Hướng dẫn sử dụng"
              aria-label="Hướng dẫn sử dụng"
              className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-300 bg-rose-600 text-white shadow-sm shadow-rose-200 active:bg-rose-700"
            >
              <Sparkles size={18} strokeWidth={2.25} aria-hidden />
            </button>
          </div>
        </div>

        {canAttendance && attendanceHint ? (
          <button
            type="button"
            data-tour="m-home-attendance"
            onClick={() => navigate("/hrm/hrm_attendance")}
            className="relative mt-4 flex w-full items-center gap-3 rounded-xl border border-l-[3px] border-violet-200 border-l-violet-600 bg-white px-3.5 py-3 text-left shadow-sm active:bg-violet-50"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-600 text-white shadow-sm shadow-violet-200">
              <Fingerprint size={18} strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-violet-800">
                Chấm công hôm nay
              </p>
              <p className="truncate text-sm font-semibold text-gray-900">
                {attendanceHint}
              </p>
            </div>
            <ChevronRight size={16} className="shrink-0 text-violet-500" />
          </button>
        ) : null}

        {canNotify && unread != null ? (
          <button
            type="button"
            data-tour="m-home-notifications"
            onClick={() => navigate("/system/notifications")}
            className="relative mt-3 flex w-full items-center gap-3 rounded-xl border border-l-[3px] border-emerald-200 border-l-emerald-600 bg-white px-3.5 py-3 text-left shadow-sm active:bg-emerald-50"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm shadow-emerald-200">
              <Bell size={18} strokeWidth={2.25} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-bold text-emerald-800">
                Thông báo
              </p>
              <p className="truncate text-sm font-semibold text-gray-900">
                {unread > 0
                  ? `${unread} chưa đọc`
                  : "Không có thông báo mới"}
              </p>
            </div>
            {unread > 0 ? (
              <span className="shrink-0 rounded-full bg-rose-600 px-2 py-0.5 text-[10px] font-black text-white">
                {unread > 99 ? "99+" : unread}
              </span>
            ) : (
              <ChevronRight size={16} className="shrink-0 text-emerald-500" />
            )}
          </button>
        ) : null}
      </div>

      <div className="space-y-6 px-5 pt-5 pb-4">
        {hasAnyModuleDash ? (
          <div data-tour="m-home-modules">
            <MobileSectionHeader
              title="Báo cáo theo module"
              tone="rose"
            />
            <p className="mb-4 -mt-1 pl-3.5 text-[11px] font-medium text-gray-600">
              Chỉ hiện nhóm bạn có quyền xem Dashboard
            </p>

            <div className="space-y-6">
              {canCrmDash ? (
                <ModuleReportSection
                  title="CRM"
                  subtitle="Khách hàng, chăm sóc, hợp đồng"
                  tone="sky"
                  hubPath="/m/crm"
                  loading={loading}
                  cards={crmCards}
                  dataTour="m-home-crm"
                  onOpenHub={() => navigate("/m/crm")}
                />
              ) : null}

              {canHrmDash ? (
                <ModuleReportSection
                  title="HRM"
                  subtitle="Nhân sự, chấm công, nghỉ phép"
                  tone="violet"
                  hubPath="/m/hrm"
                  loading={loading}
                  cards={hrmCards}
                  dataTour="m-home-hrm"
                  onOpenHub={() => navigate("/m/hrm")}
                />
              ) : null}

              {canElearnDash ? (
                <ModuleReportSection
                  title="E-Learning"
                  subtitle="Học viên, lớp, khóa học"
                  tone="indigo"
                  hubPath="/m/elearning"
                  loading={loading}
                  cards={elearnCards}
                  dataTour="m-home-elearning"
                  onOpenHub={() => navigate("/m/elearning")}
                />
              ) : null}

              {canFinanceDash ? (
                <ModuleReportSection
                  title="Tài chính"
                  subtitle="Thu-chi, tạm ứng, thanh toán"
                  tone="teal"
                  hubPath="/m/finance"
                  loading={loading}
                  cards={financeCards}
                  dataTour="m-home-finance"
                  onOpenHub={() => navigate("/m/finance")}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <PersonalAccountPanel
          loading={personalLoading}
          devices={myDevices}
          loginLogs={myLoginLogs}
          showNoDashNote={!hasAnyModuleDash}
        />
      </div>
    </MobilePageShell>
  );
}
